import { z } from 'zod';
import { garmentTypeKeySchema, measurementUnitSchema } from '../constants/garment-types.js';
import { garmentCategorySchema } from '../constants/garment-types.js';
import { nonNegativePaiseSchema } from '../money/paise.js';
import { objectIdSchema, isoDateStringSchema } from './common.js';
import { measurementSketchRefSchema } from './measurement-sketches.js';

/**
 * How a shop measures — the contract for measurement TEMPLATES.
 *
 * Every shop measures differently. A blouse in Chennai has different fields from one in Surat,
 * and an individual master tailor keeps fields nobody else uses. So measurements are
 * configurable data rather than code, and this file is the shape of that data.
 *
 * The single most important thing here is the distinction between a field's `key` and its
 * `label`. The key is an identifier that is fixed at the moment the field is created and can
 * never change; the label is what this shop calls it today. Captured values (task 9) and order
 * snapshots (task 10) are stored against the KEY, so renaming "Bust" to "Chest round" must
 * leave three years of history perfectly readable. Every request shape below addresses a field
 * BY key and carries the label as an ordinary property, which is what makes re-keying
 * impossible to express rather than merely forbidden.
 */

/** How a value is entered. `number` covers the overwhelming majority — it is a tape measure. */
export const MEASUREMENT_INPUT_TYPES = ['number', 'text', 'select'] as const;
export type MeasurementInputType = (typeof MEASUREMENT_INPUT_TYPES)[number];
export const measurementInputTypeSchema = z.enum(MEASUREMENT_INPUT_TYPES);

export const MEASUREMENT_FIELD_KEY_MAX_LENGTH = 40;
export const MEASUREMENT_LABEL_MAX_LENGTH = 60;
export const MEASUREMENT_GROUP_MAX_LENGTH = 40;
export const MEASUREMENT_HELPER_TEXT_MAX_LENGTH = 200;
export const MAX_FIELDS_PER_TEMPLATE = 80;

/**
 * A field key: lowercase snake_case, stable for the life of the field. The same rule as a
 * garment type key, and for the same reason — both are referenced by stored history.
 */
export const measurementFieldKeySchema = z
  .string()
  .trim()
  .min(2)
  .max(MEASUREMENT_FIELD_KEY_MAX_LENGTH)
  .regex(/^[a-z][a-z0-9_]*$/, 'Field keys are lowercase snake_case');

export const measurementLabelSchema = z
  .string()
  .trim()
  .min(1, 'Enter a label')
  .max(MEASUREMENT_LABEL_MAX_LENGTH)
  .transform((value) => value.replace(/\s+/g, ' '));

export const measurementGroupSchema = z
  .string()
  .trim()
  .max(MEASUREMENT_GROUP_MAX_LENGTH)
  .transform((value) => value.replace(/\s+/g, ' '));

/** One field of a template, as it is stored and as it is served. */
export const measurementFieldSchema = z.object({
  key: measurementFieldKeySchema,
  label: z.string(),
  /**
   * `null` means "whatever this template defaults to", which in turn falls back to the shop's
   * default. Storing the fallback rather than copying the resolved value is what lets a shop
   * switch from inches to centimetres in one place.
   */
  unit: measurementUnitSchema.nullable(),
  inputType: measurementInputTypeSchema,
  /** Choices for a `select` field — "full sleeve", "three-quarter", "sleeveless". */
  options: z.array(z.string()),
  /** Optional heading so a long form reads in sections. `null` is the ungrouped run. */
  group: z.string().nullable(),
  required: z.boolean(),
  helperText: z.string().nullable(),
  /** A diagram showing where to take this measurement, via the media pipeline (task 11). */
  diagramMediaId: z.string().nullable(),
  sortOrder: z.number().int(),
  /**
   * A field that is switched off. It never appears for new capture, but still renders on
   * profiles that already hold a value for it — which is the whole reason a field is
   * deactivated rather than deleted.
   */
  active: z.boolean(),
});
export type MeasurementField = z.infer<typeof measurementFieldSchema>;

/** A field with its unit resolved through the template and shop defaults — what a form reads. */
export const resolvedMeasurementFieldSchema = measurementFieldSchema.extend({
  resolvedUnit: measurementUnitSchema,
});
export type ResolvedMeasurementField = z.infer<typeof resolvedMeasurementFieldSchema>;

export const measurementTemplateSchema = z.object({
  id: objectIdSchema,
  garmentTypeKey: z.string(),
  garmentTypeLabel: z.string(),
  category: garmentCategorySchema,
  /** `null` means "the shop's default unit". */
  defaultUnit: measurementUnitSchema.nullable(),
  resolvedDefaultUnit: measurementUnitSchema,
  /**
   * What the shop charges to make one, in paise. Null means "price on the piece".
   *
   * WHY A PRICE LIVES ON A GARMENT TYPE AT ALL, when there is a whole products catalogue next
   * door. They answer different questions and a shop needs both. A PRODUCT is a thing sold — a
   * reel of thread, a metre of lining, a readymade piece — and has no body to measure. A GARMENT
   * TYPE is work the shop does, and it is already the thing a deal, a quotation and an order all
   * name. Before this field existed, picking "Blouse" on a deal meant typing ₹800 every single
   * time even though every blouse in the shop costs ₹800; the price list could not help, because
   * "Blouse stitching" in the catalogue was a different record with no link to the garment.
   *
   * NULLABLE, exactly like `product.unitPricePaise` and for the same reason: "we do bridal
   * lehengas, price on the piece" is a real answer, and a shop forced to invent a figure would
   * put a 0 on the board that quietly understates a pipeline column. A garment with no price
   * simply arrives on a deal with the price box empty and the cursor in it.
   *
   * IT IS A DEFAULT AND NEVER A CEILING. A deal line copies it and freezes the copy, so repricing
   * a garment in settings on a Tuesday cannot rewrite what open deals are worth — the same rule
   * the catalogue already follows. See `dealLineSchema`.
   */
  unitPricePaise: nonNegativePaiseSchema.nullable(),
  /** Where this template came from. A seeded template is a shop's own copy, freely editable. */
  origin: z.enum(['seed', 'custom']),
  sortOrder: z.number().int(),
  active: z.boolean(),
  fields: z.array(resolvedMeasurementFieldSchema),
  /**
   * The outline a tailor's sketch canvas opens with — this shop's own front/back drawing of the
   * garment, which arrives locked so the tailor draws over it. Null: a blank sheet.
   */
  outline: measurementSketchRefSchema.nullable().default(null),
  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type MeasurementTemplate = z.infer<typeof measurementTemplateSchema>;

export const measurementTemplateListSchema = z.object({
  templates: z.array(measurementTemplateSchema),
  /** The shop-wide default every template and field falls back to. */
  shopDefaultUnit: measurementUnitSchema,
});

// --- Requests ------------------------------------------------------------------------------

/** Creating a garment type of the shop's own. The key is derived from the label server-side. */
export const createTemplateSchema = z.object({
  garmentTypeLabel: measurementLabelSchema,
  /** Optional: let the shop pick the key explicitly. Otherwise it is slugged from the label. */
  garmentTypeKey: garmentTypeKeySchema.optional(),
  category: garmentCategorySchema.default('unisex'),
  defaultUnit: measurementUnitSchema.nullish(),
  /** What the shop charges to make one. Omitted means priced on the piece. */
  unitPricePaise: nonNegativePaiseSchema.nullish(),
  /** Start from an existing template's fields rather than from nothing. */
  copyFieldsFrom: garmentTypeKeySchema.optional(),
});
export type CreateTemplateInput = z.input<typeof createTemplateSchema>;

/**
 * Editing the template itself. Note what is absent: `garmentTypeKey`. A shop renames the
 * LABEL of a garment type; the key it is stored under is not theirs to change.
 */
export const updateTemplateSchema = z
  .object({
    garmentTypeLabel: measurementLabelSchema.optional(),
    category: garmentCategorySchema.optional(),
    defaultUnit: measurementUnitSchema.nullish(),
    /** Explicit null is a real instruction — "this one is priced on the piece". */
    unitPricePaise: nonNegativePaiseSchema.nullish(),
    active: z.boolean().optional(),
    /** A NEW sketch to use as the outline (sketches are never edited). Null removes it. */
    outlineSketchId: objectIdSchema.nullish(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type UpdateTemplateInput = z.input<typeof updateTemplateSchema>;

/** Adding a field. The key is derived from the label unless one is given explicitly. */
export const addFieldSchema = z.object({
  label: measurementLabelSchema,
  key: measurementFieldKeySchema.optional(),
  unit: measurementUnitSchema.nullish(),
  inputType: measurementInputTypeSchema.default('number'),
  options: z.array(z.string().trim().min(1).max(60)).max(30).optional(),
  group: measurementGroupSchema.nullish(),
  required: z.boolean().default(false),
  helperText: z.string().trim().max(MEASUREMENT_HELPER_TEXT_MAX_LENGTH).nullish(),
  diagramMediaId: objectIdSchema.nullish(),
});
export type AddFieldInput = z.input<typeof addFieldSchema>;

/**
 * Editing a field, addressed by its key in the URL. `key` is not a property here, which is
 * how "renaming a label can never change the key" is enforced by the shape of the request
 * rather than by a check somebody has to remember to write.
 */
export const updateFieldSchema = z
  .object({
    label: measurementLabelSchema.optional(),
    unit: measurementUnitSchema.nullish(),
    inputType: measurementInputTypeSchema.optional(),
    options: z.array(z.string().trim().min(1).max(60)).max(30).optional(),
    group: measurementGroupSchema.nullish(),
    required: z.boolean().optional(),
    helperText: z.string().trim().max(MEASUREMENT_HELPER_TEXT_MAX_LENGTH).nullish(),
    diagramMediaId: objectIdSchema.nullish(),
    active: z.boolean().optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type UpdateFieldInput = z.input<typeof updateFieldSchema>;

/**
 * The new running order, as the complete list of field keys. Sending the whole order rather
 * than "move this one up" means a drag that lands two rows down is one request, and two people
 * reordering at once cannot interleave into an order neither of them chose.
 */
export const reorderFieldsSchema = z.object({
  order: z.array(measurementFieldKeySchema).min(1),
});
export type ReorderFieldsInput = z.infer<typeof reorderFieldsSchema>;

export const setShopDefaultUnitSchema = z.object({ defaultUnit: measurementUnitSchema });

/** The 409 body when a field that is already in use is asked to be deleted. */
export const fieldInUseDetailsSchema = z.object({
  reason: z.literal('field_in_use'),
  fieldKey: z.string(),
  /** How many captured profiles hold a value for it. */
  usageCount: z.number().int().nonnegative(),
});
export type FieldInUseDetails = z.infer<typeof fieldInUseDetailsSchema>;
